import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../../../services/apiClient";
import "./PaperAssignmentCalendar.css";
import { formatScore, showStudentURL } from "../../../utils/textUtils";
import secret from "../../../secret.json";
import { retrieveCommentaryPDFAPI } from "../paperAssignmentAPI";

const normalizeDate = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getDateKey = (value) => {
  const date = normalizeDate(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;
};

const formatFullDate = (date) =>
  date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  });

const formatDateTime = (value) =>
  new Date(value).toLocaleString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatGrade = (grade) => {
  const gradeNum = parseInt(grade, 10);
  if (Number.isNaN(gradeNum)) {
    return `${grade}학년`;
  }

  if (gradeNum >= 1 && gradeNum <= 6) {
    return `초등 ${gradeNum}학년`;
  }

  if (gradeNum >= 7 && gradeNum <= 9) {
    return `중등 ${gradeNum - 6}학년`;
  }

  if (gradeNum >= 10 && gradeNum <= 12) {
    return `고등 ${gradeNum - 9}학년`;
  }

  return `${gradeNum}학년`;
};

const attitudeVariant = (attitude) => {
  if (!attitude) return "neutral";
  if (attitude === "상") return "positive";
  if (attitude === "중") return "warning";
  if (attitude === "하") return "negative";
  return "neutral";
};

const getContentLabel = (type) => {
  if (type === "lesson") return "수업";
  if (type === "exam") return "시험";
  if (type === "homework") return "과제";
  return "기록";
};

const getContentIcon = (type) => {
  if (type === "lesson") return "📖";
  if (type === "exam") return "📋";
  if (type === "homework") return "📌";
  return "📌";
};

const getLevelKey = (level) => {
  if (!level) {
    return "unknown-level";
  }
  if (level.id !== null && level.id !== undefined) {
    return String(level.id);
  }
  if (level.level_id !== null && level.level_id !== undefined) {
    return String(level.level_id);
  }
  return level.name ?? "unknown-level";
};

const getStudentKey = (student) => {
  if (!student) {
    return "unknown-student";
  }
  if (student.id !== null && student.id !== undefined) {
    return String(student.id);
  }
  if (student.student_id !== null && student.student_id !== undefined) {
    return String(student.student_id);
  }
  return student.name ?? "unknown-student";
};

// 알림장 카테고리 감지 함수
const detectDayCategories = (record) => {
  const categories = new Set();

  // class_content 검사
  if (Array.isArray(record.class_content)) {
    record.class_content.forEach((content) => {
      if (content.type === "lesson") categories.add("lesson");
      if (content.type === "exam") categories.add("exam");
      if (content.type === "homework") categories.add("homework");
    });
  }

  // homework_files 검사
  if (Array.isArray(record.homework_files) && record.homework_files.length > 0) {
    categories.add("homework_files");

    // 채점완료 검사
    const hasGraded = record.homework_files.some(
      (file) => file.student_submission?.score !== null &&
                file.student_submission?.score !== undefined
    );
    if (hasGraded) {
      categories.add("graded");
    }
  }

  // 카테고리가 없으면 "general" 추가
  if (categories.size === 0) {
    categories.add("general");
  }

  return Array.from(categories);
};

const getCategoryLabel = (category) => {
  const labels = {
    lesson: "수업",
    homework: "현장과제",
    exam: "시험",
    homework_files: "지필과제",
    graded: "채점완료",
    analysis: "지문분석",
    comprehensive: "내용이해",
    paragraph: "모의고사",
    vocabulary: "단어",
    sentence: "문장",
    grammar: "어법",
    general: "코멘트"
  };
  return labels[category] || category;
};

const PaperAssignmentCalendar = ({ paperAssignmentRecords = [], onlineAssignments = [] }) => {
  const [selectedDay, setSelectedDay] = useState(null);
  const [activeRecord, setActiveRecord] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState("all");
  const [selectedStudentId, setSelectedStudentId] = useState("all");
  const [selectedAssignmentType, setSelectedAssignmentType] = useState("all");
  const [studentNameSearch, setStudentNameSearch] = useState("");
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const hasRecords = paperAssignmentRecords.length > 0;

  const classOptions = useMemo(() => {
    const map = new Map();
    paperAssignmentRecords.forEach((record) => {
      if (!record?.level) {
        return;
      }
      const key = getLevelKey(record.level);
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: record.level?.name ?? "반 정보 없음",
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "ko-KR")
    );
  }, [paperAssignmentRecords]);

  const studentOptions = useMemo(() => {
    const map = new Map();
    paperAssignmentRecords.forEach((record) => {
      if (!record?.student) {
        return;
      }

      const recordClassKey = getLevelKey(record.level);
      if (selectedClassId !== "all" && recordClassKey !== selectedClassId) {
        return;
      }

      const studentKey = getStudentKey(record.student);
      if (!map.has(studentKey)) {
        map.set(studentKey, {
          key: studentKey,
          label: record.student?.name ?? "이름 미상",
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "ko-KR")
    );
  }, [paperAssignmentRecords, selectedClassId]);

  useEffect(() => {
    if (
      selectedStudentId !== "all" &&
      studentOptions.every((option) => option.key !== selectedStudentId)
    ) {
      setSelectedStudentId("all");
    }
  }, [studentOptions, selectedStudentId]);

  const filteredRecords = useMemo(() => {
    return paperAssignmentRecords.filter((record) => {
      if (!record?.created_at) {
        return false;
      }

      const levelKey = getLevelKey(record.level);
      const studentKey = getStudentKey(record.student);
      const studentName = record.student?.name || "";

      if (selectedClassId !== "all" && levelKey !== selectedClassId) {
        return false;
      }

      if (selectedStudentId !== "all" && studentKey !== selectedStudentId) {
        return false;
      }

      // Student name search filter
      if (studentNameSearch && !studentName.toLowerCase().includes(studentNameSearch.toLowerCase())) {
        return false;
      }

      // Assignment type filter - if online type is selected, exclude paper assignments
      if (selectedAssignmentType !== "all" && selectedAssignmentType !== "paper") {
        return false;
      }

      return true;
    });
  }, [paperAssignmentRecords, selectedClassId, selectedStudentId, studentNameSearch, selectedAssignmentType]);

  useEffect(() => {
    if (filteredRecords.length === 0) {
      setSelectedDay(null);
      setActiveRecord(null);
    }
  }, [filteredRecords.length]);

  const recordsByDate = useMemo(() => {
    return filteredRecords.reduce((acc, record) => {
      // Use class_date if available, otherwise fall back to created_at
      const displayDate = record.class_date || record.created_at;
      const key = getDateKey(displayDate);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(record);
      return acc;
    }, {});
  }, [filteredRecords]);

  // 온라인 과제 필터링 (클래스 및 학생 필터 적용, paper_assignment와 class_content 제외)
  const filteredOnlineAssignments = useMemo(() => {
    return onlineAssignments.filter((assignment) => {
      const assignmentDate = assignment?.finished_at || assignment?.created_at;
      if (!assignmentDate || !assignment?.student) {
        return false;
      }

      const assignmentType = assignment.type;

      // paper_assignment와 class_content 타입은 이미 알림장으로 표시되므로 제외
      if (assignmentType === 'paper_assignment' || assignmentType === 'class_content') {
        return false;
      }

      const levelKey = getLevelKey(assignment.level);
      const studentKey = getStudentKey(assignment.student);
      const studentName = assignment.student?.name || "";

      if (selectedClassId !== "all" && levelKey !== selectedClassId) {
        return false;
      }

      if (selectedStudentId !== "all" && studentKey !== selectedStudentId) {
        return false;
      }

      // Student name search filter
      if (studentNameSearch && !studentName.toLowerCase().includes(studentNameSearch.toLowerCase())) {
        return false;
      }

      // Assignment type filter (only for online assignments)
      if (selectedAssignmentType !== "all" && selectedAssignmentType !== "paper") {
        if (assignmentType !== selectedAssignmentType) {
          return false;
        }
      } else if (selectedAssignmentType === "paper") {
        // If "paper" is selected, exclude online assignments
        return false;
      }

      return true;
    });
  }, [onlineAssignments, selectedClassId, selectedStudentId, studentNameSearch, selectedAssignmentType]);

  // 온라인 과제를 날짜별로 그룹핑 (필터링된 과제만, finished_at 기준)
  const onlineAssignmentsByDate = useMemo(() => {
    return filteredOnlineAssignments.reduce((acc, assignment) => {
      const assignmentDate = assignment?.finished_at || assignment?.created_at;
      if (!assignmentDate) return acc;
      const key = getDateKey(assignmentDate);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(assignment);
      return acc;
    }, {});
  }, [filteredOnlineAssignments]);

  // 날짜별 카테고리 계산 (알림장 + 온라인 과제)
  const categoriesByDate = useMemo(() => {
    const result = {};

    // 알림장 카테고리 추가
    Object.keys(recordsByDate).forEach((dateKey) => {
      const records = recordsByDate[dateKey];
      const categoriesSet = new Set();

      records.forEach((record) => {
        const categories = detectDayCategories(record);
        categories.forEach((cat) => categoriesSet.add(cat));
      });

      result[dateKey] = Array.from(categoriesSet);
    });

    // 온라인 과제 카테고리 추가
    Object.keys(onlineAssignmentsByDate).forEach((dateKey) => {
      const assignments = onlineAssignmentsByDate[dateKey];
      if (!result[dateKey]) {
        result[dateKey] = [];
      }

      const categoriesSet = new Set(result[dateKey]);

      assignments.forEach((assignment) => {
        // Online assignments have type directly on the assignment object
        const type = assignment.type;
        // Skip paper_assignment and class_content types as those are already in paper assignments
        if (type && type !== 'paper_assignment' && type !== 'class_content') {
          categoriesSet.add(type);
        }
      });

      result[dateKey] = Array.from(categoriesSet);
    });

    return result;
  }, [recordsByDate, onlineAssignmentsByDate]);

  useEffect(() => {
    if (!selectedDay) {
      return;
    }

    const key = getDateKey(selectedDay);
    const hasPaperRecords = recordsByDate[key] && recordsByDate[key].length > 0;
    const hasOnlineRecords = onlineAssignmentsByDate[key] && onlineAssignmentsByDate[key].length > 0;

    // Only reset selectedDay if there are no records at all (neither paper nor online)
    if (!hasPaperRecords && !hasOnlineRecords) {
      setSelectedDay(null);
    }
  }, [recordsByDate, onlineAssignmentsByDate, selectedDay]);

  const datesWithRecords = useMemo(
    () =>
      Object.keys(recordsByDate).map((key) => {
        const [year, month, day] = key.split("-").map(Number);
        return new Date(year, month - 1, day);
      }),
    [recordsByDate]
  );

  const selectedDayKey = selectedDay ? getDateKey(selectedDay) : null;

  const selectedRecords = useMemo(() => {
    if (!selectedDayKey) {
      return [];
    }

    const paperRecords = recordsByDate[selectedDayKey] ?? [];
    const onlineRecords = onlineAssignmentsByDate[selectedDayKey] ?? [];

    // Combine and mark type
    const combinedRecords = [
      ...paperRecords.map(r => ({ ...r, _recordType: 'paper' })),
      ...onlineRecords.map(r => ({ ...r, _recordType: 'online' }))
    ];

    return combinedRecords.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  }, [recordsByDate, onlineAssignmentsByDate, selectedDayKey]);

  useEffect(() => {
    if (!activeRecord) {
      return;
    }

    const stillExists = selectedRecords.some(
      (record) => record.id === activeRecord.id
    );
    if (!stillExists) {
      setActiveRecord(null);
    }
  }, [selectedRecords, activeRecord]);

  const handleDayClick = (day) => {
    if (!day) {
      return;
    }

    const normalizedDay = normalizeDate(day);
    const key = getDateKey(normalizedDay);

    if (selectedDay && getDateKey(selectedDay) === key) {
      setSelectedDay(null);
      return;
    }

    setSelectedDay(normalizedDay);
  };

  const downloadHomeworkFile = async (s3Key) => {
    try {
      const response = await apiClient.get(
        `/api/v1/paper-assignment/download/${encodeURIComponent(s3Key)}/`,
        { withAuth: true }
      );

      if (response.data.download_url) {
        window.open(response.data.download_url, "_blank");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("파일 다운로드에 실패했습니다.");
    }
  };

  const showCommentaryPDF = async (assignmentId, studentId) => {
    try {
      const pdfWindow = window.open();
      const response = await retrieveCommentaryPDFAPI(assignmentId, studentId);
      if (response.status !== 200) {
        alert("해설지를 불러오는데 실패했습니다.");
        pdfWindow.close();
        return;
      }
      const file = new Blob([response.data], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      pdfWindow.location.href = fileURL;
    } catch (error) {
      console.error("Error loading commentary PDF:", error);
      alert("해설지를 불러오는데 실패했습니다.");
    }
  };

  const totalRecordsCount = filteredRecords.length;
  const selectedDateLabel = selectedDay ? formatFullDate(selectedDay) : null;

  // Generate calendar days for the current month
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];

    // Add empty slots for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  }, [currentMonth]);

  const changeMonth = (offset) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + offset);
      return newDate;
    });
  };

  const closeModal = () => setActiveRecord(null);
  const hasActiveFilters =
    selectedClassId !== "all" ||
    selectedStudentId !== "all" ||
    selectedAssignmentType !== "all" ||
    studentNameSearch !== "";

  const handleResetFilters = () => {
    setSelectedClassId("all");
    setSelectedStudentId("all");
    setSelectedAssignmentType("all");
    setStudentNameSearch("");
  };

  return (
    <section className="paper-assignment-calendar">
      <div className="paper-assignment-calendar__header">
        <div>
          <h4 className="paper-assignment-calendar__title">알림장 및 과제 캘린더</h4>
          <p className="paper-assignment-calendar__subtitle">
            날짜별로 등록된 알림장·과제 기록을 확인하고 상세 내용을 빠르게 검토하세요.
          </p>
        </div>
        <div className="paper-assignment-calendar__stats">
          <span className="paper-assignment-calendar__stats-number">
            {totalRecordsCount}
          </span>
          <span className="paper-assignment-calendar__stats-label">
            총 등록 건수
          </span>
        </div>
      </div>

      {hasRecords ? (
        <div className="paper-assignment-calendar__controls">
          <div className="paper-assignment-calendar__filters">
            <label className="paper-assignment-calendar__filter">
              <span className="paper-assignment-calendar__filter-label">클래스</span>
              <select
                value={selectedClassId}
                onChange={(event) => setSelectedClassId(event.target.value)}
                className="paper-assignment-calendar__select"
              >
                <option value="all">전체 클래스</option>
                {classOptions.map(({ key, label }) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            <label className="paper-assignment-calendar__filter">
              <span className="paper-assignment-calendar__filter-label">학생</span>
              <select
                value={selectedStudentId}
                onChange={(event) => setSelectedStudentId(event.target.value)}
                className="paper-assignment-calendar__select"
              >
                <option value="all">전체 학생</option>
                {studentOptions.map(({ key, label }) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            <label className="paper-assignment-calendar__filter">
              <span className="paper-assignment-calendar__filter-label">학생 이름 검색</span>
              <input
                type="text"
                value={studentNameSearch}
                onChange={(event) => setStudentNameSearch(event.target.value)}
                className="paper-assignment-calendar__select"
                placeholder="이름으로 검색..."
              />
            </label>

            <label className="paper-assignment-calendar__filter">
              <span className="paper-assignment-calendar__filter-label">과제 유형</span>
              <select
                value={selectedAssignmentType}
                onChange={(event) => setSelectedAssignmentType(event.target.value)}
                className="paper-assignment-calendar__select"
              >
                <option value="all">전체</option>
                <option value="paper">알림장</option>
                <option value="vocabulary">단어</option>
                <option value="analysis">지문분석</option>
                <option value="comprehensive">내용이해</option>
                <option value="paragraph">모의고사</option>
                <option value="sentence">문장</option>
                <option value="grammar">어법</option>
              </select>
            </label>
          </div>

          <button
            type="button"
            className="paper-assignment-calendar__filter-reset"
            onClick={handleResetFilters}
            disabled={!hasActiveFilters}
          >
            필터 초기화
          </button>
        </div>
      ) : null}

      <div className="paper-assignment-calendar__body">
        <div className="paper-assignment-calendar__picker">
          <div className="custom-calendar">
            <div className="custom-calendar__header">
              <button
                type="button"
                className="custom-calendar__nav-button"
                onClick={() => changeMonth(-1)}
              >
                ‹
              </button>
              <h3 className="custom-calendar__month-label">
                {currentMonth.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' })}
              </h3>
              <button
                type="button"
                className="custom-calendar__nav-button"
                onClick={() => changeMonth(1)}
              >
                ›
              </button>
            </div>

            <div className="custom-calendar__weekdays">
              {['일', '월', '화', '수', '목', '금', '토'].map((day) => (
                <div key={day} className="custom-calendar__weekday">
                  {day}
                </div>
              ))}
            </div>

            <div className="custom-calendar__days">
              {calendarDays.map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} className="custom-calendar__day custom-calendar__day--empty" />;
                }

                const dateKey = getDateKey(date);
                const categories = categoriesByDate[dateKey] || [];
                const isSelected = selectedDay && getDateKey(selectedDay) === dateKey;

                return (
                  <div
                    key={dateKey}
                    className={`custom-calendar__day ${isSelected ? 'custom-calendar__day--selected' : ''} ${categories.length > 0 ? 'custom-calendar__day--has-records' : ''}`}
                    onClick={() => handleDayClick(date)}
                  >
                    <div className="custom-day-content">
                      <div className="day-number">{date.getDate()}</div>
                      {categories.length > 0 && (
                        <div className="day-banners">
                          {categories.map((category) => (
                            <span key={category} className={`day-banner day-banner--${category}`}>
                              {getCategoryLabel(category)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="paper-assignment-calendar__hint">
            날짜를 클릭하면 해당일의 알림장과 과제를 확인할 수 있습니다.
          </div>
        </div>

        <div className="paper-assignment-calendar__records">
          {selectedDay ? (
            <>
              <div className="paper-assignment-calendar__selection">
                <div>
                  <p className="paper-assignment-calendar__selection-title">
                    {selectedDateLabel}
                  </p>
                  <p className="paper-assignment-calendar__selection-subtitle">
                    선택한 날짜에 등록된 기록을 확인하세요.
                  </p>
                </div>
                <span className="paper-assignment-calendar__count-badge">
                  {selectedRecords.length}건
                </span>
              </div>

              {selectedRecords.length === 0 ? (
                <div className="paper-assignment-calendar__empty">
                  이 날짜에는 등록된 알림장이나 과제가 없습니다.
                </div>
              ) : (
                <ul className="paper-assignment-calendar__list">
                  {selectedRecords.map((record) => {
                    const studentName = record.student?.name ?? "이름 미상";
                    const levelName = record.level?.name ?? "반 정보 없음";
                    const gradeLabel = record.student?.grade
                      ? formatGrade(record.student.grade)
                      : "학년 정보 없음";

                    // 온라인 과제인 경우
                    if (record._recordType === 'online') {
                      const worksheetType = record.type;
                      const worksheetTitle = record.worksheet?.title || record.title;

                      return (
                        <li
                          key={`online-${record.id}`}
                          className="paper-assignment-calendar__card"
                          style={{ backgroundColor: '#f0fff4' }}
                          role="button"
                          tabIndex={0}
                          onClick={() => setActiveRecord(record)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setActiveRecord(record);
                            }
                          }}
                        >
                          <div className="paper-assignment-calendar__card-header">
                            <div>
                              <span className="paper-assignment-calendar__student-name">
                                {studentName}
                              </span>
                              {record.level?.name && (
                                <span className="paper-assignment-calendar__student-meta">
                                  {levelName}
                                </span>
                              )}
                              {record.student?.grade && (
                                <span className="paper-assignment-calendar__student-meta">
                                  {gradeLabel}
                                </span>
                              )}
                              {worksheetTitle && (
                                <span className="paper-assignment-calendar__student-meta" style={{ fontWeight: 'bold' }}>
                                  {worksheetTitle}
                                </span>
                              )}
                            </div>
                            <div className="paper-assignment-calendar__card-chips">
                              {worksheetType && (
                                <span className={`day-banner day-banner--${worksheetType}`}>
                                  {getCategoryLabel(worksheetType)}
                                </span>
                              )}
                              <span className="paper-assignment-calendar__chip paper-assignment-calendar__chip--positive">
                                온라인 과제
                              </span>
                              <span className="paper-assignment-calendar__chip paper-assignment-calendar__chip--positive">
                                완료
                              </span>
                            </div>
                          </div>

                          <div className="paper-assignment-calendar__card-footer">
                            <span className="paper-assignment-calendar__timestamp">
                              완료일: {formatDateTime(record.finished_at || record.created_at)}
                            </span>
                            <div className="paper-assignment-calendar__card-footer-right">
                              <button
                                type="button"
                                className="paper-assignment-calendar__card-button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setActiveRecord(record);
                                }}
                              >
                                상세보기
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    }

                    // 알림장 (paper assignment)인 경우
                    const commentPreview = record.comment?.trim();
                    const homeworkCount = record.homework_files?.length ?? 0;
                    const classContentItems = Array.isArray(record.class_content)
                      ? record.class_content.filter((item) => item?.description)
                      : [];
                    const classContentPreview = classContentItems.slice(0, 2);
                    const categories = detectDayCategories(record);

                    // class_content에서 파일이 있는 항목 수 계산 (description 필터 없이 전체 확인)
                    const classContentFileCount = Array.isArray(record.class_content)
                      ? record.class_content.filter(
                          (item) => item?.file_url || item?.file_name || item?.s3_key
                        ).length
                      : 0;
                    const totalFileCount = homeworkCount + classContentFileCount;

                    return (
                      <li
                        key={record.id}
                        className="paper-assignment-calendar__card"
                        role="button"
                        tabIndex={0}
                        onClick={() => setActiveRecord(record)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setActiveRecord(record);
                          }
                        }}
                      >
                        <div className="paper-assignment-calendar__card-header">
                          <div>
                            <span className="paper-assignment-calendar__student-name">
                              {studentName}
                            </span>
                            <span className="paper-assignment-calendar__student-meta">
                              {levelName}
                            </span>
                            <span className="paper-assignment-calendar__student-meta">
                              {gradeLabel}
                            </span>
                          </div>
                          <div className="paper-assignment-calendar__card-chips">
                            <span
                              className={`paper-assignment-calendar__chip paper-assignment-calendar__chip--${
                                !record.attendance && record.attendance !== false ? "neutral" : record.attendance ? "positive" : "negative"
                              }`}
                            >
                              {!record.attendance && record.attendance !== false ? "출결 -" : record.attendance ? "출석" : "결석"}
                            </span>
                            <span
                              className={`paper-assignment-calendar__chip paper-assignment-calendar__chip--${attitudeVariant(
                                record.attitude
                              )}`}
                            >
                              태도 {record.attitude || "-"}
                            </span>
                            {categories.map((category) => (
                              <span key={category} className={`day-banner day-banner--${category}`}>
                                {getCategoryLabel(category)}
                              </span>
                            ))}
                          </div>
                        </div>

                        {commentPreview ? (
                          <p className="paper-assignment-calendar__card-comment" title={commentPreview}>
                            💬 {commentPreview}
                          </p>
                        ) : null}

                        {classContentItems.length > 0 ? (
                          <div className="paper-assignment-calendar__card-content">
                            {classContentPreview.map((item, index) => (
                              <span key={`${item.type}-${index}`}>
                                {item.description}
                              </span>
                            ))}
                            {classContentItems.length > classContentPreview.length ? (
                              <span className="paper-assignment-calendar__card-content-more">
                                외 {classContentItems.length - classContentPreview.length}건
                              </span>
                            ) : null}
                          </div>
                        ) : null}

                        <div className="paper-assignment-calendar__card-footer">
                          <span className="paper-assignment-calendar__timestamp">
                            {formatDateTime(record.class_date || record.created_at)}
                          </span>
                          <div className="paper-assignment-calendar__card-footer-right">
                            {totalFileCount > 0 && (
                              <span className="paper-assignment-calendar__card-meta">
                                📎 과제 파일 {totalFileCount}개
                              </span>
                            )}
                            <button
                              type="button"
                              className="paper-assignment-calendar__card-button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setActiveRecord(record);
                              }}
                            >
                              상세보기
                            </button>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          ) : (
            <div className="paper-assignment-calendar__placeholder">
              날짜를 선택하면 알림장 및 과제 기록이 여기에서 보여집니다.
            </div>
          )}
        </div>
      </div>

      {activeRecord ? (
        <div
          className="paper-assignment-calendar__modal-backdrop"
          role="presentation"
          onClick={closeModal}
        >
          <div
            className="paper-assignment-calendar__modal"
            role="dialog"
            aria-modal="true"
            aria-label="알림장 및 과제 상세"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="paper-assignment-calendar__modal-close"
              onClick={closeModal}
            >
              ✕
            </button>

            <header className="paper-assignment-calendar__modal-header">
              <div>
                <h3 className="paper-assignment-calendar__modal-title">
                  {activeRecord.student?.name ?? "이름 미상"}
                </h3>
                {(activeRecord.level?.name || activeRecord.student?.grade) && (
                  <p className="paper-assignment-calendar__modal-subtitle">
                    {[activeRecord.level?.name, activeRecord.student?.grade ? formatGrade(activeRecord.student.grade) : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                )}
                <p className="paper-assignment-calendar__modal-timestamp">
                  {activeRecord._recordType === 'online'
                    ? `완료일: ${formatDateTime(activeRecord.finished_at || activeRecord.created_at)}`
                    : formatDateTime(activeRecord.class_date || activeRecord.created_at)}
                </p>
              </div>
              <div className="paper-assignment-calendar__modal-chips">
                {activeRecord._recordType === 'online' ? (
                  <>
                    {activeRecord.type && (
                      <span className={`day-banner day-banner--${activeRecord.type}`}>
                        {getCategoryLabel(activeRecord.type)}
                      </span>
                    )}
                    <span className="paper-assignment-calendar__chip paper-assignment-calendar__chip--positive">
                      온라인 과제
                    </span>
                  </>
                ) : (
                  <>
                    <span
                      className={`paper-assignment-calendar__chip paper-assignment-calendar__chip--${
                        !activeRecord.attendance && activeRecord.attendance !== false ? "neutral" : activeRecord.attendance ? "positive" : "negative"
                      }`}
                    >
                      {!activeRecord.attendance && activeRecord.attendance !== false ? "출결 -" : activeRecord.attendance ? "출석" : "결석"}
                    </span>
                    <span
                      className={`paper-assignment-calendar__chip paper-assignment-calendar__chip--${attitudeVariant(
                        activeRecord.attitude
                      )}`}
                    >
                      태도 {activeRecord.attitude || "-"}
                    </span>
                    {detectDayCategories(activeRecord).map((category) => (
                      <span key={category} className={`day-banner day-banner--${category}`}>
                        {getCategoryLabel(category)}
                      </span>
                    ))}
                  </>
                )}
              </div>
            </header>

            {activeRecord._recordType === 'online' ? (
              <>
                <section className="paper-assignment-calendar__modal-section">
                  <h4 className="paper-assignment-calendar__section-title">과제 정보</h4>
                  <p className="paper-assignment-calendar__section-content">
                    <strong>제목:</strong> {activeRecord.worksheet?.title || activeRecord.title || "제목 없음"}
                  </p>
                  <p className="paper-assignment-calendar__section-content">
                    <strong>유형:</strong> {activeRecord.type ? getCategoryLabel(activeRecord.type) : "유형 없음"}
                  </p>
                  <p className="paper-assignment-calendar__section-content">
                    <strong>상태:</strong>{" "}
                    <span className="paper-assignment-calendar__chip paper-assignment-calendar__chip--positive">
                      완료
                    </span>
                  </p>
                  {activeRecord.finished_at && (
                    <p className="paper-assignment-calendar__section-content">
                      <strong>완료 시간:</strong> {formatDateTime(activeRecord.finished_at)}
                    </p>
                  )}
                </section>

                {activeRecord.commentary_pdf_path && activeRecord.commentary_pdf_path !== "" && (
                  <section className="paper-assignment-calendar__modal-section">
                    <h4 className="paper-assignment-calendar__section-title">해설지</h4>
                    <button
                      type="button"
                      onClick={() => showCommentaryPDF(activeRecord.id, activeRecord.student.id)}
                      style={{
                        display: 'inline-block',
                        padding: '10px 20px',
                        backgroundColor: '#11346A',
                        color: '#FFF',
                        border: 'none',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        textAlign: 'left'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#0F2A5A'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#11346A'}
                    >
                      📄 해설지 보기
                    </button>
                  </section>
                )}
              </>
            ) : null}

            {activeRecord.report_link ? (
              <section className="paper-assignment-calendar__modal-section">
                <h4 className="paper-assignment-calendar__section-title">알림장</h4>
                  <a
                    href={`${secret.API_BASE_URL}/paper_report/${activeRecord.report_link.token}/`.replace(/([^:]\/)\/+/g, '$1')}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px',
                      backgroundColor: '#11346A',
                      color: '#FFF',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#0F2A5A'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#11346A'}
                  >
                    📄 알림장 보기
                  </a>
              </section>
            ) : null}

            {activeRecord.student ? (
              <section className="paper-assignment-calendar__modal-section">
                <h4 className="paper-assignment-calendar__section-title">학생 학습 페이지</h4>
                <a
                  href={showStudentURL(activeRecord.student.session)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '10px 20px',
                    backgroundColor: '#11346A',
                    color: '#FFF',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#0F2A5A'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#11346A'}
                >
                  🎓 학생 학습 페이지 보기
                </a>
              </section>
            ) : null}

            {activeRecord.comment ? (
              <section className="paper-assignment-calendar__modal-section">
                <h4 className="paper-assignment-calendar__section-title">코멘트</h4>
                <p className="paper-assignment-calendar__section-content">
                  {activeRecord.comment}
                </p>
              </section>
            ) : null}

            {Array.isArray(activeRecord.class_content) &&
            activeRecord.class_content.length > 0 ? (
              <section className="paper-assignment-calendar__modal-section">
                <h4 className="paper-assignment-calendar__section-title">
                  수업 내용
                </h4>
                <ul className="paper-assignment-calendar__content-list">
                  {activeRecord.class_content.map((content, index) => {
                    const hasFile = content.file_url || content.file_name || content.s3_key;

                    return (
                      <li key={`${content.type}-${index}`}>
                        <span className="paper-assignment-calendar__content-badge">
                          {getContentLabel(content.type)}
                        </span>
                        <span>{content.description}</span>
                        {hasFile && (
                          <a
                            href={content.s3_key ? `https://pub-773bb0abd56d4402937254e0cdd4fd1c.r2.dev/${content.s3_key}` : content.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="paper-assignment-calendar__content-file-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            📎 {content.file_name || '파일 보기'}
                          </a>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}

            {Array.isArray(activeRecord.homework_files) &&
            activeRecord.homework_files.length > 0 ? (
              <section className="paper-assignment-calendar__modal-section">
                <h4 className="paper-assignment-calendar__section-title">
                  과제 파일 ({activeRecord.homework_files.length}개)
                </h4>
                <ul className="paper-assignment-calendar__file-list">
                  {activeRecord.homework_files.map((file) => {
                    // 과제 파일이 있는지 확인
                    const hasOriginalFile = file.s3_key && file.file_name;
                    // 학생 제출 파일이 있는지 확인
                    const hasSubmission = file.student_submission?.submission_s3_key;

                    return (
                      <li key={file.id}>
                        <div>
                          <p className="paper-assignment-calendar__file-title">
                            {file.title}
                          </p>
                          {file.file_name && (
                            <p className="paper-assignment-calendar__file-name">
                              {file.file_name}
                            </p>
                          )}
                          {file.student_submission ? (
                            <p className="paper-assignment-calendar__file-submission">
                              {file.student_submission.score !== null &&
                              file.student_submission.score !== undefined ? (
                                <span className="paper-assignment-calendar__chip paper-assignment-calendar__chip--positive">
                                  점수 {formatScore(file.student_submission.score)}점
                                </span>
                              ) : null}
                              <span className="paper-assignment-calendar__submission-status">
                                제출 완료
                              </span>
                            </p>
                          ) : null}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {hasOriginalFile && (
                            <button
                              type="button"
                              className="paper-assignment-calendar__download-button"
                              onClick={() => downloadHomeworkFile(file.s3_key)}
                            >
                              과제 파일 다운로드
                            </button>
                          )}
                          {hasSubmission && (
                            <button
                              type="button"
                              className="paper-assignment-calendar__download-button"
                              onClick={() => downloadHomeworkFile(file.student_submission.submission_s3_key)}
                            >
                              제출 파일 다운로드
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default PaperAssignmentCalendar;
